import * as React from "react";
import { useState } from "react";
import { styled } from "@mui/material";
import {
  DemoZoomViewState,
  useDemoReasoningController,
} from "./useDemoReasoningController";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { ReactComponent as KahunIcon } from "../../../assets/kahunIconLight.svg";
import { DemoBadge, DemoPanelTooltip } from "./demoPanelTooltip";
import { DemoZoomContentSwitch } from "./demoZoomContentSwitch";
import { MobileReasoningReferencesBox } from "./mobileReasoningReferencesBox";
import { scrollableStyleByColor } from "fe-common/ui/commonStyles";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import IconButton from "@mui/material/IconButton";
import clsx from "clsx";
import { MobileBreakpoint } from "../../../layout/breakpoints";
import { DemoModeGraphSelectionDetails } from "./demoModeGraphSelectionDetails";
//import { usePatient } from "../../../state/patientContext";

//region [[ Styles ]]
const DesktopDemoZoomContainer = styled("div")(({ theme }) => ({
  height: "100vh",
  position: "relative",
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(229, 224, 229, 1)",
  overflow: "hidden",
  [theme.breakpoints.down(MobileBreakpoint)]: {
    display: "none",
  },
  opacity: 0,
  flex: 0,
  transition: "all 1s ease-out",
  "&.open": {
    flex: 1,
    flexBasis: "60%",
    opacity: 1,
    padding: "30px",
  },
}));

const BackGroundIcon = styled("div")({
  position: "absolute",
  zIndex: 0,
});

const DesktopDemoZoomView = styled("div")({
  position: "relative",
  zIndex: 1,
  background: PatientColorMap.White,
  border: "1px solid black",
  padding: "30px",
  height: "100%",
  width: "100%",
  display: "flex",
  flexFlow: "column",
  marginTop: "22px",
  "& .icon-container": {
    position: "static",
    paddingLeft: 0,
    paddingRight: "16px",
    height: "89px",
    zIndex: 1500,
    background: PatientColorMap.White,
  },
});

const TopArea = styled("div")({
  display: "flex",
  flexFlow: "row",
  height: "30px",
});

const GoBackButtonWrapper = styled(IconButton)({
  "&.back": {
    position: "absolute",
    zIndex: 2000,
    left: "15px",
    "&:hover, &:focus, &": {
      padding: "12px",
      background: "transparent",
    },
  },
});
const MainContent = styled("div")({
  display: "flex",
  flexFlow: "column",
  justifyContent: "center",
  width: "100%",
  // flexBasis: "50%",
  maxHeight: "60%",
  position: "relative",
  marginBottom: "30px",
  marginTop: "33px",
  "& .denied-toggle": {
    width: "100%",
    flexFlow: "row-reverse",
  },
  ...scrollableStyleByColor(PatientColorMap.Primary),
  "& [data-flipper-id], & > div": {
    height: "100%",
  },
  "& .references-box": {
    paddingLeft: "117px", // Leave some room for the big icon to the left of the screen
    paddingTop: "5px", // Let the top title leave some space from the referecnes text
  },
});

const ReferencesSection = styled("div")({
  display: "flex",
  position: "relative",
  flexFlow: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  width: "100%",
  // flexBasis: "30%",
  maxHeight: "40%",
  ...scrollableStyleByColor(PatientColorMap.Primary),
  "& [data-flipper-id], & > div": {
    height: "100%",
    width: "100%",
  },
});

const IconContainer = styled("div")({
  display: "flex",
  flexFlow: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
});

const ReasoningTitle = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "center",
  alignItems: "center",
  alignContent: "center",
  width: "100%",
  fontWeight: 700,
  fontSize: "19px",
  lineHeight: "23px",
  marginTop: "33px",
  textAlign: "center",
  color: PatientColorMap.Default,
});

const CloseButton = styled(CloseIconButton)({
  "&.close-button.desktop-demo-close-icon": {
    right: "15px",
    left: "auto",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface DesktopDemoZoomProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DesktopDemoZoom = ({ ...props }: DesktopDemoZoomProps) => {
  const [title, setTitle] = useState("");

  // const { graphSelection } = usePatient();

  const {
    open,
    currentView,
    allowBack,
    ...demoReasoningContentProps
  } = useDemoReasoningController();

  // const currentBottomView = graphSelection
  //   ? DemoZoomViewState.GraphDetails
  //   : DemoZoomViewState.References;

  const renderKahunIcon = (disableTooltip = false) => (
    <IconContainer className={"icon-container"}>
      <KahunIcon />
      {!disableTooltip && <DemoPanelTooltip />}
      {disableTooltip && (
        <DemoBadge className={"demo-badge"}> Clinical Reasoning </DemoBadge>
      )}
    </IconContainer>
  );

  return (
    <DesktopDemoZoomContainer
      className={clsx("demo-container", open ? "open" : "")}
    >
      <BackGroundIcon>{renderKahunIcon(true)}</BackGroundIcon>
      {/*<Grow in={open} timeout={150} mountOnEnter unmountOnExit>*/}
      <DesktopDemoZoomView>
        <TopArea>
          {renderKahunIcon()}
          {/*<Fade in={allowBack} mountOnEnter unmountOnExit>*/}
          <GoBackButtonWrapper
            className={"back"}
            onClick={demoReasoningContentProps.onGoBack}
          >
            <ArrowBackIosIcon />
          </GoBackButtonWrapper>
          {/*</Fade>*/}
          <ReasoningTitle>{title}</ReasoningTitle>
          <CloseButton
            onClick={demoReasoningContentProps.onCloseClick}
            className={"desktop-demo-close-icon"}
          />
        </TopArea>
        <MainContent className={"main-content"}>
          <DemoZoomContentSwitch
            currentView={currentView}
            setTitle={setTitle}
            desktop
            {...demoReasoningContentProps}
          />
        </MainContent>
        {currentView !== DemoZoomViewState.Loading && (
          <ReferencesSection className={"references-section"}>
            {/*<Fade*/}
            {/*  in={currentBottomView === DemoZoomViewState.References}*/}
            {/*  mountOnEnter*/}
            {/*  unmountOnExit*/}
            {/*>*/}
            <MobileReasoningReferencesBox
              currentSelectedDisease={
                demoReasoningContentProps.currentSelectedDisease
              }
              onReferencesClick={demoReasoningContentProps.onReferencesClick}
            />
            {/*</Fade>*/}
            {/*<Fade*/}
            {/*  in={currentBottomView === DemoZoomViewState.GraphDetails}*/}
            {/*  mountOnEnter*/}
            {/*  unmountOnExit*/}
            {/*>*/}
            <DemoModeGraphSelectionDetails {...props} />
            {/*</Fade>*/}
          </ReferencesSection>
        )}
      </DesktopDemoZoomView>
      {/*</Grow>*/}
    </DesktopDemoZoomContainer>
  );
};
